import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaChevronRight, FaEye, FaEyeSlash, FaSearch, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getApplicationsList, downloadApplicationsExcel } from '../services/subscriberWalletService';
import { useAuth } from '../auth/AuthContext';
import { formatDateOnly } from '../utils/dateFormatter';
import { FaFileExcel } from 'react-icons/fa';
import CreditBulkActions from '../components/subscriber/CreditBulkActions';
import { SUBSCRIBER_LIST_URL_KEY } from '../utils/subscriberHelpers';

// Scroll offset saved when a row is opened, keyed by the list URL it was opened from,
// so Back lands on the same rows rather than the top of the page.
const SCROLL_KEY = 'subscriberList:scroll';

const SubscriberList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // State for API data
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Excel download state
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [excelMessage, setExcelMessage] = useState(null);
  
  // Search state - initialize from URL or empty
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState(() => searchParams.get('date_filter') || '');
  const [startDate, setStartDate] = useState(() => searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(() => searchParams.get('end_date') || '');
  
  // Convert string dates to Date objects for DatePicker
  const startDateObj = startDate ? new Date(startDate) : null;
  const endDateObj = endDate ? new Date(endDate) : null;
  
  // Helper to convert Date to YYYY-MM-DD string
  const dateToString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Custom DatePicker Input Component
  const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, clearDate }, ref) => (
    <div className="relative w-full">
      <button
        type="button"
        onClick={onClick}
        ref={ref}
        className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm text-left hover:border-gray-400 transition-colors"
      >
        <span className={value ? 'text-gray-900 block truncate pr-8' : 'text-gray-400 block truncate pr-8'}>
          {value || placeholder}
        </span>
      </button>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
        {value && clearDate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearDate();
            }}
            className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100 pointer-events-auto"
            aria-label="Clear date"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        )}
        <FaCalendarAlt className="text-gray-400 w-4 h-4 pointer-events-none" />
      </div>
    </div>
  ));
  CustomDateInput.displayName = 'CustomDateInput';
  
  // Auto-detect role from authentication
  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance', 
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    return roleMapping[authRole] || null;
  })();
  
  const PAGE_SIZE = 25;
  // Initialize page from URL or default to 1
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [pagination, setPagination] = useState({
    page: 1,
    items: PAGE_SIZE,
    total: 0,
    total_pages: 0
  });
  
  const isOpsOrSuperAdmin = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const isSuperAdmin = currentRole === 'SuperAdmin';
  const isFinanceOrSupport = currentRole === 'Finance' || currentRole === 'Support';
  const canAccessDownloadExcel = currentRole === 'SuperAdmin' || currentRole === 'Finance';
  // For Ops and SuperAdmin: start with masked, can toggle. For Finance and Support: always masked (no toggle)
  const [mobilesUnmasked, setMobilesUnmasked] = useState(false);
  
  // Track if initial mount is complete to prevent duplicate calls
  const isInitialMountRef = useRef(true);
  // Track last API call parameters to prevent duplicate calls
  const lastApiCallRef = useRef({});
  // Track if API call is in progress to prevent concurrent calls
  const isLoadingRef = useRef(false);
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (page > 1) params.set('page', page.toString());
    if (search.trim()) params.set('search', search.trim());
    if (dateFilter) params.set('date_filter', dateFilter);
    if (dateFilter === 'custom' && startDate) params.set('start_date', startDate);
    if (dateFilter === 'custom' && endDate) params.set('end_date', endDate);
    
      setSearchParams(params, { replace: true });
  }, [page, search, dateFilter, startDate, endDate, setSearchParams]);

  // Handle search submission (Enter key or debounce)
  const handleSearchSubmit = useCallback(() => {
    const newSearch = searchInput.trim();
    if (newSearch !== search) {
      setSearch(newSearch);
      setPage(1); // Reset to page 1 when search changes
    }
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [searchInput, search]);

  // Debounce search input (2 seconds)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearchSubmit();
    }, 2000);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, handleSearchSubmit]);

  const maskMobile = (value) => {
    if (!value) return 'N/A';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length <= 4) return `******${digits}`;
    const last4 = digits.slice(-4);
    return `******${last4}`;
  };
  
  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setPage(1);
    if (value !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };
  
  // Validate date range is exactly 31 days or less
  const validateDateRangeForDownload = () => {
    if (!startDate || !endDate) {
      return { valid: false, message: 'Please select both start date and end date' };
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 because both dates are inclusive
    
    if (diffDays > 31) {
      return { valid: false, message: 'Date range cannot exceed 31 days' };
    }
    
    if (diffDays < 1) {
      return { valid: false, message: 'End date must be after start date' };
    }
    
    return { valid: true };
  };
  
  // Handle Excel download - only works with custom date range; SuperAdmin and Finance only
  const handleDownloadExcel = async () => {
    if (!canAccessDownloadExcel) return;
    // Validate that custom range is selected
    if (dateFilter !== 'custom') {
      setExcelMessage({ type: 'error', text: 'Please select Custom Range to download Excel' });
      setTimeout(() => {
        setExcelMessage(null);
      }, 5000);
      return;
    }
    
    // Validate dates are selected and range is valid
    const validation = validateDateRangeForDownload();
    if (!validation.valid) {
      setExcelMessage({ type: 'error', text: validation.message });
      setTimeout(() => {
        setExcelMessage(null);
      }, 5000);
      return;
    }
    
    setDownloadingExcel(true);
    setExcelMessage(null);
    
    try {
      // Only send custom date range filters (no search, no other filters)
      const filters = {
        start_date: startDate,
        end_date: endDate
      };
      
      const response = await downloadApplicationsExcel(currentRole, filters);
      
      // Check if it's a successful WhatsApp notification
      if (response?.metadata?.api_status) {
        const message = response?.msg?.message || 'Excel file will be sent to your WhatsApp shortly';
        setExcelMessage({ type: 'success', text: message });
        
        // Clear message after 5 seconds
        setTimeout(() => {
          setExcelMessage(null);
        }, 5000);
      } else {
        // Handle error response
        const errorMsg = response?.msg?.error || response?.msg?.status || 'Failed to generate Excel file';
        setExcelMessage({ type: 'error', text: errorMsg });
        
        // Clear message after 5 seconds
        setTimeout(() => {
          setExcelMessage(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      let errorMessage = 'Failed to download Excel file. Please try again.';
      
      // Try to extract error message from error
      if (error.message) {
        try {
          const errorMatch = error.message.match(/\{[\s\S]*\}/);
          if (errorMatch) {
            const errorData = JSON.parse(errorMatch[0]);
            errorMessage = errorData?.msg?.error || errorData?.msg?.status || errorMessage;
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Request timeout. Please check your connection and try again.';
          } else {
            errorMessage = error.message;
          }
        } catch {
          errorMessage = error.message;
        }
      }
      
      setExcelMessage({ type: 'error', text: errorMessage });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setExcelMessage(null);
      }, 5000);
    } finally {
      setDownloadingExcel(false);
    }
  };
  
  // Check if download button should be enabled
  const canDownloadExcel = dateFilter === 'custom' && startDate && endDate && validateDateRangeForDownload().valid;
  
  // Load applications from API with server-side pagination
  useEffect(() => {
    // Skip on initial mount if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Skip if not authenticated or no role
    if (!isAuthenticated || !currentRole) {
      return;
    }
    
    // Build API params
    const apiParams = {
      page: page || 1,
      items: PAGE_SIZE
    };
    
    if (search.trim()) apiParams.search = search.trim();
    if (dateFilter) {
      apiParams.date_filter = dateFilter;
      if (dateFilter === 'custom' && startDate && endDate) {
        apiParams.start_date = startDate;
        apiParams.end_date = endDate;
      }
    }
    
    // Create a key for comparison
    const apiCallKey = JSON.stringify(apiParams);
    
    // Prevent duplicate API calls with same parameters
    if (lastApiCallRef.current.key === apiCallKey) {
      return;
    }
    
    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }
    
    // Update last API call reference
    lastApiCallRef.current = { key: apiCallKey, params: apiParams };
    
    const loadApplications = async () => {
      // Mark as loading
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await getApplicationsList(currentRole, apiParams);
        
        let applicationsData = null;
        let paginationData = null;
        
        // Handle response structure: { msg: { items: [...], pagination: {...} }, metadata: {...} }
        if (response && response.msg) {
          if (response.msg.items && Array.isArray(response.msg.items)) {
            applicationsData = response.msg.items;
          }
          if (response.msg.pagination) {
            paginationData = response.msg.pagination;
          }
        } else if (response && response.data) {
          if (response.data.items && Array.isArray(response.data.items)) {
            applicationsData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
        } else if (response && response.success && response.data) {
          if (response.data.items && Array.isArray(response.data.items)) {
            applicationsData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
        } else if (Array.isArray(response)) {
          // Fallback: if response is directly an array
          applicationsData = response;
          paginationData = {
            page: 1,
            items: response.length,
            total: response.length,
            total_pages: Math.ceil(response.length / PAGE_SIZE)
          };
        }
        
        if (applicationsData && Array.isArray(applicationsData)) {
          setApplications(applicationsData);
          if (paginationData) {
            // Calculate total_pages from total and items (backend doesn't provide total_pages)
            const calculatedTotalPages = Math.ceil(paginationData.total / paginationData.items);
            setPagination({
              ...paginationData,
              total_pages: calculatedTotalPages
            });
          } else {
            // Default pagination if not provided (shouldn't happen if backend is correct)
            setPagination({
              page: page || 1,
              items: PAGE_SIZE,
              total: applicationsData.length,
              total_pages: Math.ceil(applicationsData.length / PAGE_SIZE)
            });
          }
        } else {
          setApplications([]);
          setPagination({
            page: 1,
            items: PAGE_SIZE,
            total: 0,
            total_pages: 0
          });
          setError(`Failed to load applications - Response: ${JSON.stringify(response).substring(0, 200)}...`);
        }
      } catch (error) {
        console.error('Error loading applications:', error);
        setError('Failed to load applications');
        setApplications([]);
        setPagination({
          page: 1,
          items: PAGE_SIZE,
          total: 0,
          total_pages: 0
        });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        // Mark initial mount as complete after first successful load
        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      }
    };

    loadApplications();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search, dateFilter, startDate, endDate]);

  // Restore the scroll offset once the rows it was measured against are back.
  useEffect(() => {
    if (loading || applications.length === 0) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved) sessionStorage.removeItem(SCROLL_KEY);
      if (!saved || saved.search !== location.search) return;
      // The table settles over a few frames; keep nudging until the page is tall enough.
      let frames = 0;
      const restore = () => {
        window.scrollTo(0, saved.y);
        if (Math.abs(window.scrollY - saved.y) > 2 && frames++ < 30) requestAnimationFrame(restore);
      };
      requestAnimationFrame(restore);
    } catch {
      // sessionStorage unavailable — Back still keeps page and filters via the URL.
    }
  }, [loading, applications, location.search]);

  const openSubscriber = (path) => {
    const from = `${location.pathname}${location.search}`;
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search: location.search, y: window.scrollY }));
      sessionStorage.setItem(SUBSCRIBER_LIST_URL_KEY, from);
    } catch {
      // ignore — scroll restore is a convenience
    }
    navigate(path, { state: { from } });
  };

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/subscriber-wallet')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="mr-2">Subscribers</span>
              </h2>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <CreditBulkActions isSuperAdmin={isSuperAdmin} currentRole={currentRole} />
                {canAccessDownloadExcel && (
                  <button
                    onClick={handleDownloadExcel}
                    disabled={downloadingExcel || !canDownloadExcel}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      downloadingExcel || !canDownloadExcel
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'bg-green-600 text-white border-green-600 hover:bg-green-700 hover:border-green-700'
                    }`}
                    title={
                      !canDownloadExcel
                        ? 'Please select Custom Range with start and end dates (max 31 days) to download Excel'
                        : 'Download Excel file for selected date range'
                    }
                  >
                    <FaFileExcel className="w-4 h-4" />
                    {downloadingExcel ? 'Downloading...' : 'Download Excel'}
                  </button>
                )}
              </div>
            </div>
            {canAccessDownloadExcel && excelMessage && (
              <div className={`mt-3 px-4 py-2 rounded-md text-sm ${
                excelMessage.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {excelMessage.text}
              </div>
            )}
            <div className="mt-3">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Name, Mobile, or REF CODE..."
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Date Filter */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleDateFilterChange('')}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    dateFilter === ''
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                  }`}
                >
                  All Dates
                </button>
                <button
                  onClick={() => handleDateFilterChange('today')}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    dateFilter === 'today'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => handleDateFilterChange('yesterday')}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    dateFilter === 'yesterday'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                  }`}
                >
                  Yesterday
                </button>
                <button
                  onClick={() => handleDateFilterChange('last_7_days')}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    dateFilter === 'last_7_days'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                  }`}
                >
                  Last 7 Days
                </button>
                <button
                  onClick={() => handleDateFilterChange('custom')}
                  className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    dateFilter === 'custom'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                  }`}
                >
                  Custom Range
                </button>
              </div>
              
              {/* Custom Date Range */}
              {dateFilter === 'custom' && (() => {
                // Calculate max date for end date (startDate + 30 days = 31 days total including both dates)
                const getMaxEndDate = () => {
                  if (!startDateObj) return null;
                  const maxDate = new Date(startDateObj);
                  maxDate.setDate(maxDate.getDate() + 30);
                  return maxDate;
                };

                // Calculate min date for start date (endDate - 30 days = 31 days total including both dates)
                const getMinStartDate = () => {
                  if (!endDateObj) return null;
                  const minDate = new Date(endDateObj);
                  minDate.setDate(minDate.getDate() - 30);
                  return minDate;
                };

                // Validate date range doesn't exceed 31 days (30 days difference = 31 days total)
                const validateDateRange = (newStart, newEnd) => {
                  if (newStart && newEnd) {
                    const diffTime = Math.abs(newEnd - newStart);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays <= 30; // 30 days difference = 31 days total (inclusive)
                  }
                  return true;
                };

                return (
                <div className="mt-3 flex gap-3">
                    <div className="w-48">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                      <DatePicker
                        selected={startDateObj}
                        onChange={(date) => {
                          if (!date) {
                            setStartDate('');
                            setPage(1);
                            return;
                          }
                          
                          // Ensure start date is not after today
                          const today = new Date();
                          today.setHours(23, 59, 59, 999); // End of today
                          if (date > today) {
                            setExcelMessage({ type: 'error', text: 'Start date cannot be after today' });
                            setTimeout(() => {
                              setExcelMessage(null);
                            }, 3000);
                            return;
                          }
                          
                          if (!endDateObj || validateDateRange(date, endDateObj)) {
                            setStartDate(dateToString(date));
                            setPage(1);
                          } else {
                            // If new start date makes range > 31 days, adjust end date
                            const maxEnd = new Date(date);
                            maxEnd.setDate(maxEnd.getDate() + 30); // 30 days difference = 31 days total
                            // Ensure adjusted end date is not after today
                            const today = new Date();
                            today.setHours(23, 59, 59, 999);
                            if (maxEnd > today) {
                              maxEnd.setTime(today.getTime());
                            }
                            setStartDate(dateToString(date));
                            setEndDate(dateToString(maxEnd));
                        setPage(1);
                          }
                        }}
                        selectsStart
                        startDate={startDateObj}
                        endDate={endDateObj}
                        maxDate={(() => {
                          const today = new Date();
                          const maxFromEnd = endDateObj;
                          // Return the earlier of: today or endDateObj
                          return maxFromEnd && maxFromEnd < today ? maxFromEnd : today;
                        })()}
                        minDate={getMinStartDate()}
                        placeholderText="Select start date"
                        dateFormat="dd/MM/yyyy"
                        customInput={<CustomDateInput clearDate={() => { setStartDate(''); setPage(1); }} />}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        className="w-full"
                    />
                  </div>
                      <div className="w-48">
                    <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                      <DatePicker
                        selected={endDateObj}
                        onChange={(date) => {
                          if (!date) {
                            setEndDate('');
                            setPage(1);
                            return;
                          }
                          
                          // Ensure end date is not after today
                          const today = new Date();
                          today.setHours(23, 59, 59, 999); // End of today
                          if (date > today) {
                            setExcelMessage({ type: 'error', text: 'End date cannot be after today' });
                            setTimeout(() => {
                              setExcelMessage(null);
                            }, 3000);
                            return;
                          }
                          
                          if (!startDateObj || validateDateRange(startDateObj, date)) {
                            setEndDate(dateToString(date));
                            setPage(1);
                          } else {
                            // If new end date makes range > 31 days, adjust start date
                            const minStart = new Date(date);
                            minStart.setDate(minStart.getDate() - 30); // 30 days difference = 31 days total
                            setStartDate(dateToString(minStart));
                            setEndDate(dateToString(date));
                        setPage(1);
                          }
                        }}
                        selectsEnd
                        startDate={startDateObj}
                        endDate={endDateObj}
                        minDate={startDateObj}
                        maxDate={(() => {
                          const today = new Date();
                          const maxEndFromStart = getMaxEndDate();
                          // Return the earlier of: today or maxEndFromStart (startDate + 30 days)
                          return maxEndFromStart && maxEndFromStart < today ? maxEndFromStart : today;
                        })()}
                        placeholderText="Select end date"
                        dateFormat="dd/MM/yyyy"
                        customInput={<CustomDateInput clearDate={() => { setEndDate(''); setPage(1); }} />}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        className="w-full"
                    />
                  </div>
                </div>
                );
              })()}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <span>Mobile</span>
                      {isOpsOrSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => setMobilesUnmasked((v) => !v)}
                          className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                          title={mobilesUnmasked ? 'Mask all mobile numbers' : 'Unmask all mobile numbers'}
                        >
                          {mobilesUnmasked ? <FaEyeSlash className="w-3 h-3" /> : <FaEye className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REF CODE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Family Members</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                      {loading ? 'Loading...' : 'No subscribers found'}
                    </td>
                  </tr>
                ) : (
                  applications.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openSubscriber(`/subscriber-wallet/subscriber/${encodeURIComponent(r.id)}`)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateOnly(r.start_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {r.personal?.full_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(isFinanceOrSupport || !mobilesUnmasked)
                          ? (r.mobile_display || maskMobile(r.personal?.mobile || r.mobile))
                          : (r.personal?.mobile || r.mobile || r.mobile_display || 'N/A')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.ref_code || r.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.plan || r.personal_subscription || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          (r.status === 'Active' || r.status === 'active' || r.active) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {r.status || (r.active ? 'Active' : 'Inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₹{(r.wallet_balance || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.family_count || r.family_members?.length || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-400">
                        <FaChevronRight />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.items) + 1}-{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Prev
                </button>
                {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                  const pageNum = i + 1;
                  // Show first page, last page, current page, and pages around current
                  const totalPages = pagination.total_pages || 1;
                  const shouldShow = 
                    pageNum === 1 || 
                    pageNum === totalPages || 
                    (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                  
                  if (!shouldShow) {
                    // Show ellipsis
                    if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                      return (
                        <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }
                    return null;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriberList;

